import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);

  const scrollToTopAndNavigate = (path: string) => {
    setMobileOpen(false);
    setMobileCatOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(path);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-background-200/60">
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2 cursor-pointer" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
            <span className="font-heading text-xl font-semibold text-foreground-900 tracking-tight">
              Sugargoo<span className="text-primary-500">Sheet</span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-6">
            <a href="/" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
              <i className="ri-home-line mr-1.5 w-3.5 h-3.5 inline-flex items-center justify-center"></i>
              Home
            </a>
            <a href="/sugargoo-spreadsheet-guide" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap" onClick={(e) => { e.preventDefault(); navigate('/sugargoo-spreadsheet-guide'); }}>
              Spreadsheet
            </a>
            <a href="/sugargoo-shopping-guide" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap" onClick={(e) => { e.preventDefault(); navigate('/sugargoo-shopping-guide'); }}>
              <i className="ri-shopping-bag-line mr-1.5 w-3.5 h-3.5 inline-flex items-center justify-center"></i>
              Shopping Guide
            </a>
            <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap">
              Categories
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
              Visit Main Website
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 max-h-[70vh] overflow-y-auto">
          <div className="px-6 py-5 space-y-1">
            <a
              href="/"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              onClick={(e) => { e.preventDefault(); scrollToTopAndNavigate('/'); }}
            >
              <i className="ri-home-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
              Home
            </a>
            <a
              href="/sugargoo-shopping-guide"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              onClick={(e) => { e.preventDefault(); scrollToTopAndNavigate('/sugargoo-shopping-guide'); }}
            >
              <i className="ri-shopping-bag-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
              Shopping Guide
            </a>
            <a
              href="/sugargoo-spreadsheet-guide"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              onClick={(e) => { e.preventDefault(); scrollToTopAndNavigate('/sugargoo-spreadsheet-guide'); }}
            >
              <i className="ri-file-list-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
              Spreadsheet Guide
            </a>

            <button
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
                Categories
              </span>
              <i className={`${mobileCatOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} w-4 h-4 flex items-center justify-center text-foreground-400 transition-transform duration-200`}></i>
            </button>

            {mobileCatOpen && (
              <div className="pl-10 space-y-1 pt-1 pb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-medium text-foreground-600 hover:bg-accent-50 hover:text-accent-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-foreground-400`}></i>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <div className="border-t border-background-200/60 my-3"></div>

            <div className="px-1 space-y-1">
              <p className="px-3 py-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider">SE Articles</p>
              <a href="/sugargoo-shipping-guide" className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-foreground-700 hover:bg-secondary-50 hover:text-secondary-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                onClick={(e) => { e.preventDefault(); scrollToTopAndNavigate('/sugargoo-shipping-guide'); }}>
                <i className="ri-ship-line w-5 h-5 flex items-center justify-center text-foreground-400"></i> Shipping Guide
              </a>
              <a href="/sugargoo-review" className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-foreground-700 hover:bg-secondary-50 hover:text-secondary-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                onClick={(e) => { e.preventDefault(); scrollToTopAndNavigate('/sugargoo-review'); }}>
                <i className="ri-star-line w-5 h-5 flex items-center justify-center text-foreground-400"></i> Sugargoo Review
              </a>
              <a href="/sugargoo-coupon" className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-foreground-700 hover:bg-secondary-50 hover:text-secondary-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                onClick={(e) => { e.preventDefault(); scrollToTopAndNavigate('/sugargoo-coupon'); }}>
                <i className="ri-coupon-line w-5 h-5 flex items-center justify-center text-foreground-400"></i> Coupons
              </a>
              <a href="/sugargoo-legit" className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-foreground-700 hover:bg-secondary-50 hover:text-secondary-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                onClick={(e) => { e.preventDefault(); scrollToTopAndNavigate('/sugargoo-legit'); }}>
                <i className="ri-shield-check-line w-5 h-5 flex items-center justify-center text-foreground-400"></i> Is Sugargoo Legit?
              </a>
              <a href="/sugargoo-warehouse" className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-foreground-700 hover:bg-secondary-50 hover:text-secondary-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                onClick={(e) => { e.preventDefault(); scrollToTopAndNavigate('/sugargoo-warehouse'); }}>
                <i className="ri-store-line w-5 h-5 flex items-center justify-center text-foreground-400"></i> Warehouse Guide
              </a>
            </div>

            <div className="pt-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Visit Main Website
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}